import React from "react";
import * as XLSX from "xlsx";
import type { ColumnsType } from "antd/es/table";

interface ExportExcelProps<T> {
  columns: ColumnsType<T>;
  dataSource: T[];
  fileName?: string;
  buttonLabel?: string;
  className?: string;
}

function ExportExcel<T extends Record<string, any>>({
  columns,
  dataSource,
  fileName = "report",
  buttonLabel = "Export Excel",
  className = "btn btn-primary",
}: ExportExcelProps<T>) {
  const handleExport = () => {
    // Only export columns that have a real dataIndex + title (skip action/render-only columns)
    const exportableColumns = columns.filter(
      (col: any) => col.dataIndex && col.title
    );

    const headers = exportableColumns.map((col: any) => col.title as string);
    const keys = exportableColumns.map((col: any) => col.dataIndex as string);

    const rows = dataSource.map((record) =>
      keys.map((key) => {
        const value = record[key];
        return value === undefined || value === null ? "" : value;
      })
    );

    const worksheetData = [headers, ...rows];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Auto-size columns roughly based on content length
    worksheet["!cols"] = headers.map((header, i) => {
      const maxLen = Math.max(
        header.length,
        ...rows.map((r) => String(r[i] ?? "").length)
      );
      return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
    });

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Report");

    const timestamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `${fileName}_${timestamp}.xlsx`);
  };

  return (
    <button type="button" className={className} onClick={handleExport}>
      <i className="ti ti-file-export me-1" />
      {buttonLabel}
    </button>
  );
}

export default ExportExcel;